import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatApiErrorDetail } from '../lib/utils';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Reset link sent!');
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="forgot-password-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] py-4">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="DCA Enterprises" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/login" 
              className="text-sm text-[#475569] hover:text-[#1D4ED8] flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="flex-grow flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 border border-[#E2E8F0] shadow-sm">
            {!submitted ? (
              <>
                <div className="text-center mb-8">
                  <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-2" data-testid="forgot-password-title">
                    Forgot Password?
                  </h1>
                  <p className="text-[#475569] text-sm">
                    Enter your email and we'll send you a reset link.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="forgot-password-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                  <div>
                    <label htmlFor="forgot-email" className="block text-sm font-medium text-[#0A0F1C] mb-2">Email <span className="text-red-500" aria-hidden="true">*</span></label>
                    <div className="relative">
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-input pl-10"
                        data-testid="forgot-password-email-input"
                        required
                        autoComplete="email"
                        aria-required="true"
                      />
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-primary-blue w-full"
                    data-testid="forgot-password-submit-button"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center" data-testid="forgot-password-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h2 className="heading-3 text-xl text-[#0A0F1C] mb-2">Check Your Email</h2>
                <p className="text-[#475569] text-sm mb-6">
                  If an account exists with that email, you'll receive a password reset link shortly.
                </p>
                <Link 
                  to="/login"
                  className="text-[#1D4ED8] font-medium hover:underline"
                >
                  Back to Login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
